/**
 * Detail (docs/11 §3.5) — the one screen a driver reads while parked. Opens
 * instantly from the row's data; the occurrence fetch only enriches. Every
 * number carries its basis in words (docs/10 principle 3); confidence is
 * never a percentage. Primary action sits in the thumb zone.
 */
import { useEffect, useState } from 'react';
import { ChevronLeft, ChevronRight, Check, MapPin, Flag, Info, CarFront } from 'lucide-react';
import { api, type Detail, type Opp } from './api.js';
import { Button, InlineWarn } from './ui.js';
import { Countdown } from './EventCard.js';
import {
  fmtWindow, fmtTime, demandLabel, etaLabel, headingLabel, categoryLabel, fmtDistance, basisText,
  REASON_TEXT, dayPrefix,
} from './format.js';

const MODE_LABEL: Record<string, string> = {
  taxi: 'taxis', rideshare: 'rideshare', livery: 'livery', private_hire: 'private hire',
};

export function DetailScreen({ opp, now, units, hasOrigin, driverMode, tripActiveTitle, busy, error, onBack, onHeadThere }: {
  opp: Opp;
  now: number;
  units: 'mi' | 'km';
  /** False when the driver's position is unknown: no timing claims. */
  hasOrigin: boolean;
  driverMode: string;
  /** Title of the active trip, if any: Head there becomes Switch to this. */
  tripActiveTitle: string | null;
  busy: boolean;
  error: string | null;
  onBack: () => void;
  onHeadThere: () => void;
}) {
  const [detail, setDetail] = useState<Detail | null>(null);
  const [reported, setReported] = useState(false);
  const venue = opp.occurrence.venue_name ?? opp.occurrence.title;
  const day = dayPrefix(opp.egress.p10_at, now);

  useEffect(() => {
    let alive = true;
    api.occurrence(opp.occurrence.id)
      .then((d) => { if (alive) setDetail(d); })
      .catch(() => { /* the row's data is enough to render */ });
    return () => { alive = false; };
  }, [opp.occurrence.id]);

  async function reportExiting() {
    try { await api.crowdExiting(opp.occurrence.id); } catch { /* rate-limited: fine */ }
    setReported(true);
  }

  const zones = detail?.staging_zones ?? [{
    zone_id: opp.staging.zone_id, label: opp.staging.label, approach: opp.staging.approach,
    accessibility: null, disclaimer: opp.staging.disclaimer, permitted_modes: [],
    lat: opp.staging.lat, lng: opp.staging.lng,
  }];
  const attendance = detail?.attendance_est ?? null;

  return (
    <div className="screen">
      <header className="hdr">
        <button className="back" onClick={onBack}><ChevronLeft size={24} aria-hidden="true" /> Tonight</button>
      </header>
      <h1 className="title">{venue}</h1>
      {opp.occurrence.venue_name && (
        <p className="sub">{opp.occurrence.title} · {categoryLabel(opp.occurrence.category)}</p>
      )}

      <section className="card">
        {opp.leave_by ? (
          <>
            <span className="label">Leave in</span>
            <Countdown target={opp.leave_by} now={now} size="hero" />
            <span className="sub num">by {fmtTime(opp.leave_by)}</span>
          </>
        ) : (
          <span className="sub">{!hasOrigin ? 'Turn on location for a leave-by time' : 'Too far to make this one in time'}</span>
        )}
        <div className="meta num" style={{ marginTop: 6 }}>
          <span style={{ color: 'var(--text-1)', fontWeight: 600 }}>
            Crowd out {fmtWindow(opp.egress.p10_at, opp.egress.p90_at)}{day ? ` · ${day}` : ''}
          </span>
        </div>
        <div className="meta small">
          <Info size={16} aria-hidden="true" />
          <span>Most likely {fmtTime(opp.egress.p50_at)} · {basisText(opp)}</span>
        </div>
        <div className="meta small">
          <span>{hasOrigin ? `${etaLabel(opp.eta_band)} · ` : ''}{fmtDistance(opp.distance_km, units)} from where you’re looking</span>
        </div>
        {hasOrigin && opp.eta_band !== 'unknown' && (
          <p className="small muted">Drive time estimated from distance — live traffic isn’t connected yet.</p>
        )}
      </section>

      <section>
        <h2 className="heading" style={{ marginBottom: 8 }}>Why this one</h2>
        <ul className="why">
          {opp.reason_codes.filter((rc) => REASON_TEXT[rc]).map((rc) => (
            <li key={rc}><Check size={18} aria-hidden="true" /><span>{REASON_TEXT[rc]!(opp.heading)}</span></li>
          ))}
          <li><CarFront size={18} aria-hidden="true" /><span>{headingLabel(opp.heading)}</span></li>
          <li><Check size={18} aria-hidden="true" /><span>{demandLabel(opp.demand_band)}{attendance ? ` · about ${attendance.toLocaleString()} people` : ''}</span></li>
        </ul>
      </section>

      <section>
        <h2 className="heading" style={{ marginBottom: 8 }}>Pickup for {MODE_LABEL[driverMode] ?? driverMode}</h2>
        {zones.map((z, i) => (
          <div key={z.zone_id} className="inset" style={{ marginBottom: 8 }}>
            <MapPin size={18} aria-hidden="true" />
            <div className="zone">
              <b>{z.label}</b>
              {z.approach && <span>{z.approach}</span>}
              {z.accessibility && <span>{z.accessibility}</span>}
              {i === 0 && <span className="disclaimer">{z.disclaimer}</span>}
            </div>
          </div>
        ))}
        {attendance !== null && (
          <p className="small muted">
            ~{attendance.toLocaleString()} attending
            {detail?.attendance_source?.startsWith('capacity') ? ' (from venue capacity)' : ''}
          </p>
        )}
      </section>

      <div className="sticky-cta">
        {error && <InlineWarn danger>{error}</InlineWarn>}
        {tripActiveTitle && !error && (
          <p className="inline-note" style={{ marginBottom: 8 }}>You’re heading to {tripActiveTitle}. This will replace it.</p>
        )}
        <Button variant="primary" busy={busy} onClick={onHeadThere} icon={<ChevronRight size={20} aria-hidden="true" />}>
          {tripActiveTitle ? 'Switch to this' : 'Head there'}
        </Button>
        <Button variant="text" onClick={() => void reportExiting()} disabled={reported}
          icon={<Flag size={18} aria-hidden="true" />} style={{ marginTop: 6 }}>
          {reported ? 'Thanks — noted' : 'Crowd is coming out now'}
        </Button>
      </div>
    </div>
  );
}
